import React, { useEffect, useState } from 'react';
import { apiFetch } from '../apiClient';
import { FALLBACK_LEGAL } from '../constants/legalFallback';
import PublicAuthShell from '../components/PublicAuthShell';
import './LegalDocumentPage.css';

/**
 * Full-page Terms or Privacy Policy (replaces cramped modal).
 */
export default function LegalDocumentPage({
  type = 'terms',
  onBack,
  backLabel = 'Go back',
  onGoHome,
  onLogin,
  onRegister,
  onShowTerms,
  onShowPrivacy,
}) {
  const [legal, setLegal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadNote, setLoadNote] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [type]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadNote('');
      try {
        const res = await apiFetch('/public/legal');
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (res.ok && data?.terms?.version && data?.privacy?.version) {
          setLegal(data);
          return;
        }
        throw new Error(data.message || 'Could not load legal documents.');
      } catch (e) {
        if (!cancelled) {
          setLegal(FALLBACK_LEGAL);
          setLoadNote(e.message || 'Showing offline copy — start the backend on port 8001 for the latest text.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const doc = type === 'privacy' ? legal?.privacy : legal?.terms;
  const otherType = type === 'privacy' ? 'terms' : 'privacy';
  const otherLabel = type === 'privacy' ? 'Terms of Service' : 'Privacy Policy';

  return (
    <PublicAuthShell
      active="home"
      showNav
      authTheme
      centerVertically={false}
      onHome={onGoHome}
      onLogin={onLogin}
      onRegister={onRegister}
    >
      <article className="legal-page">
        <button type="button" className="legal-page__back" onClick={onBack}>
          ← {backLabel}
        </button>

        {loading ? (
          <div className="legal-page__loading" aria-live="polite">
            Loading document…
          </div>
        ) : doc ? (
          <>
            <header className="legal-page__header auth-animate">
              <p className="legal-page__badge">{type === 'privacy' ? 'Legal' : 'Legal'}</p>
              <h1 className="legal-page__title">{doc.title}</h1>
              {doc.lastUpdated ? (
                <p className="legal-page__updated">Last updated: {doc.lastUpdated}</p>
              ) : null}
            </header>

            {loadNote ? (
              <p className="legal-page__note auth-animate auth-animate--d1" role="status">
                {loadNote}
              </p>
            ) : null}

            <div className="legal-page__body">
              {(doc.sections || []).map((section, i) => (
                <section
                  key={section.heading}
                  className={`legal-page__section auth-animate auth-animate--d${Math.min(i + 2, 6)}`}
                >
                  <h2>{section.heading}</h2>
                  <p>{section.body}</p>
                </section>
              ))}
            </div>

            <footer className="legal-page__footer">
              <button type="button" className="legal-page__btn legal-page__btn--primary" onClick={onBack}>
                {backLabel}
              </button>
              <button
                type="button"
                className="legal-page__btn legal-page__btn--ghost"
                onClick={otherType === 'terms' ? onShowTerms : onShowPrivacy}
              >
                Read {otherLabel}
              </button>
            </footer>
          </>
        ) : (
          <p className="legal-page__error">Could not load this document. Please try again later.</p>
        )}
      </article>
    </PublicAuthShell>
  );
}
